import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * Horizontal action row that guarantees flex-wrap + consistent gap across
 * breakpoints. Prevents the common "5 buttons on a 360 px screen fall off
 * the right edge" bug from the legacy app.
 *
 * Align `end` puts actions on the right on md+ and wraps them to a new row
 * (still right-aligned) on xs/sm. `between` puts `leading` on the left and
 * `children` on the right, collapsing to a stacked column on xs.
 */
export interface ToolbarProps {
  children: ReactNode
  /** Extra content pinned to the opposite side; falls below on xs. */
  leading?: ReactNode
  align?: 'start' | 'end' | 'between'
  className?: string
}

export function Toolbar({ children, leading, align = 'start', className }: ToolbarProps) {
  if (leading !== undefined || align === 'between') {
    return (
      <div
        className={cn(
          'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
          className,
        )}
      >
        <div className="flex flex-wrap items-center gap-2">{leading}</div>
        <div className="flex flex-wrap items-center gap-2">{children}</div>
      </div>
    )
  }
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2',
        align === 'end' && 'justify-end',
        className,
      )}
    >
      {children}
    </div>
  )
}
